import type { MessageKey } from './en';

/** German catalogue. Informal address ("du") — this is a personal shelf. */
export const de: Record<MessageKey, string> = {
  // --- Navigation ---------------------------------------------------------
  'nav.collection': 'Sammlung',
  'nav.insights': 'Auswertung',
  'nav.add': 'Hinzufügen',
  'nav.wishlist': 'Wunschliste',
  'nav.settings': 'Einstellungen',
  'nav.skipToContent': 'Zum Inhalt springen',

  // --- Shared -------------------------------------------------------------
  'common.cancel': 'Abbrechen',
  'common.save': 'Speichern',
  'common.saving': 'Wird gespeichert…',
  'common.delete': 'Löschen',
  'common.edit': 'Bearbeiten',
  'common.done': 'Fertig',
  'common.close': 'Schließen',
  'common.back': 'Zurück',
  'common.remove': 'Entfernen',
  'common.add': 'Hinzufügen',
  'common.search': 'Suchen',
  'common.clear': 'Leeren',
  'common.loading': 'Wird geladen…',
  'common.retry': 'Erneut versuchen',
  'common.optional': 'optional',
  'common.none': 'Keine',
  'common.copy': 'Kopieren',
  'common.copied': 'Kopiert',
  'common.signOut': 'Abmelden',
  'common.apply': 'Übernehmen',
  'common.reset': 'Zurücksetzen',
  'common.more': 'Mehr',

  // --- Disc formats -------------------------------------------------------
  'format.4k': '4K UHD',
  'format.hd': 'Full HD',
  'format.combo': '4K + Full HD',
  'format.4k.short': '4K',
  'format.hd.short': 'HD',
  'format.combo.short': '4K+HD',
  'format.combo.hint': 'Eine Hülle, zwei Discs',
  'format.4k.hint': 'Eine einzelne UHD-Disc',
  'format.hd.hint': 'Eine einzelne Blu-ray',
  'format.label': 'Format',

  // --- Sign in ------------------------------------------------------------
  'auth.signIn.title': 'Physical Movies',
  'auth.signIn.tagline': 'Jede Disc im Regal und jedes Mal, wenn du sie gesehen hast.',
  'auth.signIn.button': 'Mit Google anmelden',
  'auth.signIn.busy': 'Google wird geöffnet…',
  'auth.signIn.error': 'Die Anmeldung wurde nicht abgeschlossen. Versuch es noch einmal.',
  'auth.signIn.popupBlocked':
    'Dein Browser hat das Anmeldefenster blockiert. Erlaube Pop-ups und versuch es erneut.',
  'auth.signIn.errorCode': 'Fehlercode: {code}',
  'auth.locked.title': 'Diese Sammlung ist privat',
  'auth.locked.body':
    'Du bist als {email} angemeldet, aber diese Instanz öffnet sich nur für ihre Besitzerin oder ihren Besitzer. Bis dein Konto freigeschaltet ist, bleibt hier alles verschlossen.',
  'auth.locked.ownerTitle': 'Ist das deine Instanz?',
  'auth.locked.ownerBody': 'Führe das im Projektordner aus und lade die Seite neu:',
  'auth.locked.uidLabel': 'Deine Nutzer-ID',
  'auth.locked.copyUid': 'Nutzer-ID kopieren',
  'auth.loading': 'Zugriff wird geprüft…',

  // --- Collection ---------------------------------------------------------
  'collection.title': 'Sammlung',
  'collection.summary_one': '{count} Film · {discs} Discs',
  'collection.summary': '{count} Filme · {discs} Discs',
  'collection.searchPlaceholder': 'Titel, Regie, Edition suchen',
  'collection.searchLabel': 'Sammlung durchsuchen',
  'collection.empty.title': 'Noch nichts im Regal',
  'collection.empty.body': 'Trag deine erste Disc ein, dann füllt sich die Wand.',
  'collection.empty.action': 'Film hinzufügen',
  'collection.noResults.title': 'Keine Treffer',
  'collection.noResults.body': 'Such nach etwas anderem oder setz die Filter zurück.',
  'collection.clearFilters': 'Filter zurücksetzen',
  'collection.filters': 'Filter',
  'collection.filtersActive': 'Filter ({count})',
  'collection.sort': 'Sortierung',
  'collection.surprise': 'Überrasch mich',
  'collection.results_one': '{count} Treffer',
  'collection.results': '{count} Treffer',
  'collection.lentOutBanner_one': '{count} Exemplar ist verliehen',
  'collection.lentOutBanner': '{count} Exemplare sind verliehen',

  'filter.title': 'Filter',
  'filter.format': 'Format',
  'filter.watchState': 'Gesehen',
  'filter.loanState': 'Verfügbarkeit',
  'filter.genres': 'Genres',
  'filter.any': 'Alle',
  'filter.watched': 'Gesehen',
  'filter.unwatched': 'Nie gesehen',
  'filter.loaned': 'Verliehen',
  'filter.onShelf': 'Im Regal',

  'sort.title': 'Titel',
  'sort.added': 'Zuletzt hinzugefügt',
  'sort.releaseDate': 'Erscheinungsdatum',
  'sort.lastWatched': 'Zuletzt gesehen',
  'sort.watchCount': 'Wie oft gesehen',
  'sort.runtime': 'Laufzeit',
  'sort.ascending': 'Aufsteigend',
  'sort.descending': 'Absteigend',

  // --- Movie detail -------------------------------------------------------
  'movie.logWatch': 'Gesehen eintragen',
  'movie.logWatchDetails': 'Details ergänzen',
  'movie.copies': 'Exemplare',
  'movie.copies_one': 'Exemplar',
  'movie.addCopy': 'Exemplar hinzufügen',
  'movie.watchHistory': 'Verlauf',
  'movie.neverWatched': 'Nie gesehen',
  'movie.watchedTimes_one': 'Einmal gesehen',
  'movie.watchedTimes': '{count}-mal gesehen',
  'movie.lastWatched': 'Zuletzt gesehen am {date}',
  'movie.directedBy': 'Regie: {names}',
  'movie.overview': 'Handlung',
  'movie.note': 'Deine Notiz',
  'movie.notePlaceholder': 'Wo gekauft, von wem geschenkt, was auch immer.',
  'movie.delete': 'Film löschen',
  'movie.deleteConfirm.title': '{title} löschen?',
  'movie.deleteConfirm.body_one':
    'Damit verschwinden der Film, seine Exemplare und 1 Eintrag im Verlauf. Das lässt sich nicht rückgängig machen.',
  'movie.deleteConfirm.body':
    'Damit verschwinden der Film, seine Exemplare und {count} Einträge im Verlauf. Das lässt sich nicht rückgängig machen.',
  'movie.deleted': '{title} gelöscht',
  'movie.refresh': 'Von TMDB aktualisieren',
  'movie.refreshed': 'Angaben aktualisiert',
  'movie.notFound.title': 'Film nicht gefunden',
  'movie.notFound.body': 'Vielleicht wurde er auf einem anderen Gerät gelöscht.',
  'movie.backToCollection': 'Zurück zur Sammlung',
  'movie.unavailable': 'Alle Exemplare sind verliehen',
  'movie.runtimeUnknown': 'Laufzeit unbekannt',
  'movie.addRuntime': 'Trag eine Laufzeit ein, damit der Film in die Sehzeit zählt.',

  // --- Copies -------------------------------------------------------------
  'copy.title': 'Exemplar',
  'copy.edition': 'Edition',
  'copy.editionPlaceholder': 'Steelbook, Mediabook, Director’s Cut…',
  'copy.purchasedAt': 'Gekauft am',
  'copy.notes': 'Notizen',
  'copy.notesPlaceholder': 'Schuber, Region, Teil einer Box…',
  'copy.add': 'Exemplar hinzufügen',
  'copy.added': 'Exemplar hinzugefügt',
  'copy.updated': 'Exemplar aktualisiert',
  'copy.remove': 'Exemplar entfernen',
  'copy.removed': 'Exemplar entfernt',
  'copy.removeConfirm': 'Dieses Exemplar aus dem Regal nehmen?',
  'copy.discs_one': '1 Disc',
  'copy.discs': '{count} Discs',
  'copy.lendOut': 'Verleihen',
  'copy.lending': 'Exemplar verleihen',
  'copy.borrower': 'Wer hat es',
  'copy.borrowerPlaceholder': 'Ein Name',
  'copy.lentTo': 'Bei {name}',
  'copy.lentSince': 'seit {date}',
  'copy.since': 'Seit',
  'copy.markReturned': 'Als zurück markieren',
  'copy.returned': 'Wieder im Regal',
  'copy.lent': 'Als verliehen markiert',
  'copy.lentNeedsName': 'Trag einen Namen ein, damit du weißt, wo es steckt.',

  // --- Watch logging ------------------------------------------------------
  'watch.title': 'Gesehen eintragen',
  'watch.when': 'Wann',
  'watch.now': 'Gerade eben',
  'watch.rating': 'Bewertung',
  'watch.ratingValue': '{count} von 5',
  'watch.clearRating': 'Bewertung entfernen',
  'watch.note': 'Notiz',
  'watch.notePlaceholder': 'Was hängen geblieben ist',
  'watch.submit': 'Eintragen',
  'watch.logged': 'Eingetragen',
  'watch.deleteEntry': 'Eintrag löschen',
  'watch.deleted': 'Eintrag gelöscht',
  'watch.deleteConfirm': 'Diesen Eintrag löschen?',
  'watch.futureDate': 'Wähl ein Datum, das schon vorbei ist.',
  'watch.empty': 'Noch nichts eingetragen.',

  // --- Adding a movie -----------------------------------------------------
  'filter.location': 'Ort',

  // --- Orte ------------------------------------------------------------------
  'settings.locations': 'Orte',
  'settings.locations.hint':
    'Wo die Scheiben tatsächlich stehen — damit klar ist, was heute Abend in Reichweite ist.',
  'settings.locations.empty':
    'Noch keine Orte. Leg den ersten an, und alles, was du schon besitzt, zählt als dort stehend.',
  'settings.locations.add': 'Ort hinzufügen',
  'settings.locations.namePlaceholder': 'Wohnzimmer',
  'settings.locations.save': 'Hinzufügen',
  'settings.locations.default': 'Standard',
  'settings.locations.makeDefault': 'Als Standard festlegen',
  'settings.locations.defaultHint':
    'Eine Scheibe ohne eigenen Ort zählt als hier stehend — also alles, was vor dieser Einstellung dazugekommen ist.',
  'settings.locations.rename': '{name} umbenennen',
  'settings.locations.remove': '{name} löschen',
  'settings.locations.added': '{name} hinzugefügt',
  'settings.locations.removed': '{name} gelöscht',
  'settings.locations.deleteTitle': '{name} löschen?',
  'settings.locations.deleteBody':
    'Die dort stehenden Scheiben werden nicht gelöscht — sie fallen auf den Standardort zurück.',
  'settings.locations.deleteAction': 'Ort löschen',
  'copy.location': 'Wo sie steht',

  // --- Drucken ---------------------------------------------------------------
  'collection.print': 'Drucken oder als PDF sichern',
  'print.title': 'Sammlung',
  'print.subtitle': '{count} Filme · {discs} Scheiben',
  'print.action': 'Drucken',
  'print.back': 'Zurück',
  'print.preparing': 'Cover werden geladen…',
  'print.preparingCount': 'Cover werden geladen… {loaded} von {total}',
  'print.printing': 'Seiten werden vorbereitet…',
  'print.hint': 'Drucken — oder im Druckdialog „Als PDF sichern“ wählen.',
  'print.filtered': 'Gefiltert: {summary}',
  'print.empty': 'Zu den aktuellen Filtern passt nichts.',
  'add.title': 'Film hinzufügen',
  'add.searchPlaceholder': 'Nach einem Titel suchen',
  'add.searchHint': 'Die Ergebnisse kommen von TMDB.',
  'add.searching': 'Wird gesucht…',
  'add.noResults': 'Keine Treffer bei TMDB.',
  'add.trending.title': 'Diese Woche angesagt',
  'add.trending.hint':
    'Filme, die im Kino liefen. Serien und reine Streaming-Veröffentlichungen bleiben außen vor.',
  'add.trending.loading': 'Angesagte Filme werden geladen…',
  'add.trending.failed': 'Angesagte Filme konnten nicht geladen werden.',
  'add.trending.empty': 'Diese Woche ist nichts Angesagtes im Kino gelaufen.',
  'add.manual': 'Stattdessen von Hand eintragen',
  'add.manualTitle': 'Von Hand eintragen',
  'add.field.title': 'Titel',
  'add.field.titleDe': 'Deutscher Titel',
  'add.field.year': 'Jahr',
  'add.field.runtime': 'Laufzeit in Minuten',
  'add.field.director': 'Regie',
  'add.titleRequired': 'Ein Titel muss sein.',
  'add.chooseFormat': 'Was steckt in der Hülle?',
  'add.confirm': 'Zur Sammlung hinzufügen',
  'add.added': '{title} hinzugefügt',
  'add.alreadyOwned': 'Steht schon in deinem Regal',
  'add.addAnotherCopy': 'Weiteres Exemplar hinzufügen',
  'add.openExisting': 'Öffnen',
  'add.tmdbDisabled':
    'Die TMDB-Suche ist aus. Trag VITE_TMDB_READ_TOKEN in .env.local ein, um zu suchen, oder leg Filme von Hand an.',
  'add.toWishlist': 'Auf die Wunschliste',
  'add.loadFailed': 'Dieser Titel ließ sich nicht von TMDB laden.',

  // --- Insights -----------------------------------------------------------
  'insights.title': 'Auswertung',
  'insights.tonight': 'Vorschlag für heute Abend',
  'insights.shelf': 'Dein Regal',
  'insights.shelfHint': 'Alle Discs nach Format.',
  'insights.watchTime': 'Sehzeit',
  'insights.perMonth': 'Filme pro Monat',
  'insights.topMovies': 'Am häufigsten gesehen',
  'insights.ratings': 'Deine Bewertungen',
  'insights.genres': 'Genres',
  'insights.decades': 'Jahrzehnte',
  'insights.empty.title': 'Noch nichts zu messen',
  'insights.empty.body': 'Trag einen Film als gesehen ein, dann fangen die Zahlen hier an.',
  'insights.stat.movies': 'Filme',
  'insights.stat.discs': 'Discs',
  'insights.stat.copies': 'Hüllen',
  'insights.stat.watches': 'Einträge',
  'insights.stat.unwatched': 'Nie gesehen',
  'insights.stat.avgRating': 'Durchschnitt',
  'insights.stat.lentOut': 'Verliehen',
  'insights.stat.totalTime': 'Insgesamt',
  'insights.stat.thisYear': 'Dieses Jahr',
  'insights.watchedShare': '{percent} des Regals hast du gesehen',
  'insights.busiestMonth': 'Stärkster Monat: {month}',
  'insights.noRatings': 'Noch keine Bewertungen.',
  'insights.starsAxis': '{count}★',
  'insights.moviesCounted': '{count} Filme',
  'insights.watchesCounted': '{count} Einträge',

  // --- Recommendations ----------------------------------------------------
  'rec.title': 'Vorschlag für heute Abend',
  'rec.neverWatched': 'Nie gesehen',
  'rec.longAgo': 'Vor {days} Tagen zuletzt gesehen',
  'rec.ownedFor': 'Seit {days} Tagen im Regal',
  'rec.empty.title': 'Nichts verstaubt',
  'rec.empty.body': 'Alles im Regal lief vor Kurzem. Schönes Problem.',
  'rec.another': 'Nächster Vorschlag',
  'rec.seeAll': 'Alle {count} ansehen',
  'rec.listTitle': 'Verstaubt im Regal',

  // --- Wishlist -----------------------------------------------------------
  'wishlist.title': 'Wunschliste',
  'wishlist.empty.title': 'Die Liste ist leer',
  'wishlist.empty.body': 'Merk dir die Discs, hinter denen du noch her bist.',
  'wishlist.add': 'Auf die Wunschliste',
  'wishlist.added': '{title} auf die Wunschliste gesetzt',
  'wishlist.desiredFormat': 'Gewünschtes Format',
  'wishlist.remove': 'Von der Wunschliste nehmen',
  'wishlist.removed': 'Von der Wunschliste genommen',
  'wishlist.bought': 'Habe ich gekauft',
  'wishlist.moved': '{title} steht jetzt in der Sammlung',
  'wishlist.count_one': '{count} Titel',
  'wishlist.count': '{count} Titel',
  'wishlist.note': 'Notiz',
  'wishlist.notePlaceholder': 'Welche Edition, welcher Preis, wo.',

  // --- Wishlist filters and trash -----------------------------------------
  'wishlist.filters': 'Filter',
  'wishlist.buyable': 'Verfügbarkeit',
  'wishlist.buyable.all': 'Alle',
  'wishlist.buyable.buyable': 'Schon erhältlich',
  'wishlist.buyable.upcoming': 'Noch nicht erschienen',
  'wishlist.desiredFormatFilter': 'Gewünschtes Format',
  'wishlist.markBuyable': 'Als erhältlich markieren',
  'wishlist.markUpcoming': 'Als noch nicht erschienen markieren',
  'wishlist.markedBuyable': 'Als erhältlich markiert',
  'wishlist.markedUpcoming': 'Als noch nicht erschienen markiert',
  'wishlist.upcomingBadge': 'Noch nicht erschienen',
  'wishlist.results_one': '{count} Titel',
  'wishlist.results': '{count} Titel',
  'wishlist.noResults.title': 'Keine Treffer',
  'wishlist.noResults.body': 'Such nach etwas anderem oder setz die Filter zurück.',
  'wishlist.sort.title': 'Titel',
  'wishlist.sort.added': 'Zuletzt hinzugefügt',
  'wishlist.sort.releaseDate': 'Erscheinungsdatum',
  'wishlist.sort.runtime': 'Laufzeit',
  'wishlist.deleteConfirm.title': '{title} entfernen?',
  'wishlist.deleteConfirm.body':
    'Der Eintrag wandert in den Papierkorb und bleibt dort {days} Tage, du kannst ihn also zurückholen.',
  'wishlist.deleteConfirm.action': 'In den Papierkorb',
  'wishlist.trash': 'Papierkorb',
  'wishlist.trash.open': 'Papierkorb ({count})',
  'wishlist.trash.back': 'Zurück zur Wunschliste',
  'wishlist.trash.empty.title': 'Der Papierkorb ist leer',
  'wishlist.trash.empty.body': 'Was du entfernst, wartet hier erst {days} Tage.',
  'wishlist.trash.daysLeft_one': 'Noch 1 Tag',
  'wishlist.trash.daysLeft': 'Noch {count} Tage',
  'wishlist.trash.restore': 'Zurückholen',
  'wishlist.trash.restored': '{title} ist wieder auf der Wunschliste',
  'wishlist.trash.deleteForever': 'Endgültig löschen',
  'wishlist.trash.deleteForeverConfirm.title': '{title} endgültig löschen?',
  'wishlist.trash.deleteForeverConfirm.body': 'Das lässt sich nicht rückgängig machen.',
  'wishlist.trash.deletedForever': 'Endgültig gelöscht',
  'wishlist.movedToTrash': '{title} in den Papierkorb verschoben',

  // --- Settings -----------------------------------------------------------
  'settings.title': 'Einstellungen',
  'settings.appearance': 'Darstellung',
  'settings.theme': 'Erscheinungsbild',
  'settings.theme.system': 'Wie das System',
  'settings.theme.light': 'Hell',
  'settings.theme.dark': 'Dunkel',
  'settings.language': 'Sprache',
  'settings.language.system': 'Wie das System',
  'settings.language.en': 'English',
  'settings.language.de': 'Deutsch',
  'settings.data': 'Deine Daten',
  'settings.export': 'Sammlung exportieren',
  'settings.exportHint':
    'Lädt eine JSON-Datei mit allen Filmen, Exemplaren, Einträgen und Wünschen herunter.',
  'settings.exported': 'Export heruntergeladen',
  'settings.import': 'Datei importieren',
  'settings.importHint':
    'Führt eine exportierte Datei mit dieser Sammlung zusammen. Vorhandene Einträge werden aktualisiert, nicht verdoppelt.',
  'settings.importing': 'Wird importiert…',
  'settings.imported':
    '{movies} Filme, {watches} Einträge und {wishlist} Wünsche wurden importiert.',
  'settings.importFailed': 'Diese Datei ließ sich nicht lesen: {reason}',
  'settings.account': 'Konto',
  'settings.signedInAs': 'Angemeldet als {email}',
  'settings.about': 'Über die App',
  'settings.version': 'Version',
  'settings.source': 'Quellcode',
  'settings.tmdbCredit':
    'Filmdaten und Artwork von TMDB. Dieses Produkt wird von TMDB nicht unterstützt oder zertifiziert.',

  // --- Errors and notices -------------------------------------------------
  'error.generic': 'Da ist etwas schiefgegangen. Versuch es noch einmal.',
  'error.permission': 'Dein Konto darf diese Sammlung nicht ändern.',
  'error.offline': 'Keine Verbindung zu Firestore. Änderungen werden nicht gespeichert.',
  'error.notConfigured.title': 'Firebase ist nicht konfiguriert',
  'error.notConfigured.body':
    'Kopiere .env.example nach .env.local, trag deine Firebase-Web-Konfiguration ein und starte den Dev-Server neu.',
  'error.missingKeys': 'Fehlt noch',

  // --- Units --------------------------------------------------------------
  'unit.minutesShort': '{count} Min.',
  'unit.hoursShort': '{count} Std.',
  'unit.hoursMinutes': '{hours} Std. {minutes} Min.',
  'unit.days_one': '1 Tag',
  'unit.days': '{count} Tage',
  'unit.never': 'nie',
};
